// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "defaultTypeName": "Enums", "path": "../Enums" }

import type { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';

import * as DateHelpers from '../../../utils/dateHelpers';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import { type Company, type CompanyWireFormat, NewCompany } from '../Company/Company';
import { type Contact, type ContactWireFormat, NewContact } from '../Contact/Contact';
import { Currency, type CurrencyWireFormat, NewCurrency } from '../Currency/Currency';
import Enums from '../Enums';
import {
  type IntegrationSummary,
  type IntegrationSummaryWireFormat,
  NewIntegrationSummary,
} from '../IntegrationSummary/IntegrationSummary';
import { NewProject, Project, type ProjectWireFormat } from '../Project/Project';
import {
  NewQuoteCompetitor,
  type QuoteCompetitor,
  type QuoteCompetitorWireFormat,
} from '../QuoteCompetitor/QuoteCompetitor';
import {
  NewQuoteConfig,
  type QuoteConfig,
  type QuoteConfigWireFormat,
} from '../QuoteConfig/QuoteConfig';
import {
  NewQuoteExportAttempt,
  type QuoteExportAttempt,
  type QuoteExportAttemptWireFormat,
} from '../QuoteExportAttempt/QuoteExportAttempt';
import {
  NewQuoteExportBlocker,
  type QuoteExportBlocker,
  type QuoteExportBlockerWireFormat,
} from '../QuoteExportBlocker/QuoteExportBlocker';
import {
  NewQuoteExternalID,
  type QuoteExternalID,
  type QuoteExternalIDWireFormat,
} from '../QuoteExternalID/QuoteExternalID';
import {
  NewQuotePlant,
  type QuotePlant,
  type QuotePlantWireFormat,
} from '../QuotePlant/QuotePlant';
import {
  NewQuotePriceEscalation,
  type QuotePriceEscalation,
  type QuotePriceEscalationWireFormat,
} from '../QuotePriceEscalation/QuotePriceEscalation';
import {
  NewQuoteProduct,
  type QuoteProduct,
  type QuoteProductWireFormat,
} from '../QuoteProduct/QuoteProduct';
import {
  NewQuoteRevisionOrigin,
  type QuoteRevisionOrigin,
  type QuoteRevisionOriginWireFormat,
} from '../QuoteRevisionOrigin/QuoteRevisionOrigin';
import {
  NewQuoteStatus,
  QuoteStatus,
  type QuoteStatusWireFormat,
} from '../QuoteStatus/QuoteStatus';
import { NewUser, User, type UserWireFormat } from '../User/User';

export class Quote {
  readonly id: string;
  readonly quoteNumber: string;
  readonly purchaseOrderNumber: string | null;
  readonly projectJobNumber: string | null;
  readonly name: string | null;
  readonly revisionNumber: number;
  readonly version: number | null;
  readonly status: QuoteStatus;
  readonly externalID: string | null;
  readonly restrictOrdersToProjectProducts: boolean;
  readonly restrictOrdersToProjectMixProducts: boolean;
  readonly terms: string | null;
  readonly notes: string | null;
  readonly expirationDate: DateTime | null;
  readonly project: Project;
  readonly plants: QuotePlant[];
  readonly company: Company | null;
  readonly contacts: Contact[];
  readonly quoteConfig: QuoteConfig | null;
  readonly products: QuoteProduct[];
  readonly priceEscalations: QuotePriceEscalation[];
  readonly user: User;
  readonly integrationSummary: IntegrationSummary | null;
  readonly externalIDs: QuoteExternalID[];
  readonly exportAttempts: QuoteExportAttempt[];
  readonly exportBlockers: QuoteExportBlocker[];
  readonly competitor: QuoteCompetitor | null;
  readonly divisionId: string | null;
  readonly creationDate: DateTime | null;
  readonly revisionDate: DateTime | null;
  readonly firstSentAt: DateTime | null;
  readonly lastSentAt: DateTime | null;
  readonly revenue: Currency;
  readonly estimatedVolume: number;
  readonly readyMixQuantity: string;
  readonly readyMixQuantityUnit: Enums.Unit;
  readonly aggregateQuantity: string;
  readonly aggregateQuantityUnit: Enums.Unit;
  readonly asphaltQuantity: string;
  readonly asphaltQuantityUnit: Enums.Unit;
  readonly lastStatusChangeAt: DateTime | null;
  readonly lastStatusChangeBy: User | null;
  readonly revisionOrigin: QuoteRevisionOrigin | null;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.quoteNumber = data.quoteNumber === undefined ? '' : data.quoteNumber;
    this.purchaseOrderNumber =
      data.purchaseOrderNumber === undefined ? null : data.purchaseOrderNumber;
    this.projectJobNumber = data.projectJobNumber === undefined ? null : data.projectJobNumber;
    this.name = data.name === undefined ? null : data.name;
    this.revisionNumber = data.revisionNumber === undefined ? 0 : data.revisionNumber;
    this.version = data.version === undefined ? null : data.version;
    this.status = NewQuoteStatus(data.status);
    this.externalID =
      // eslint-disable-next-line no-nested-ternary
      (data.externalID ?? null) === null
        ? null
        : data.externalID === undefined
          ? ''
          : data.externalID;
    this.restrictOrdersToProjectProducts =
      data.restrictOrdersToProjectProducts === undefined
        ? false
        : data.restrictOrdersToProjectProducts;
    this.restrictOrdersToProjectMixProducts =
      data.restrictOrdersToProjectMixProducts === undefined
        ? false
        : data.restrictOrdersToProjectMixProducts;
    this.terms = data.terms === undefined ? null : data.terms;
    this.notes = data.notes === undefined ? null : data.notes;
    this.expirationDate = DateHelpers.ParseServerNullableDate(data.expirationDate);
    this.project = NewProject(data.project);
    this.plants = (data.plants ?? []).map((plants: any) => NewQuotePlant(plants));
    this.company =
      // eslint-disable-next-line no-nested-ternary
      (data.company ?? null) === null ? null : NewCompany(data.company);
    this.contacts = (data.contacts ?? []).map((contacts: any) => NewContact(contacts));
    this.quoteConfig =
      // eslint-disable-next-line no-nested-ternary
      (data.quoteConfig ?? null) === null ? null : NewQuoteConfig(data.quoteConfig);
    this.products = (data.products ?? []).map((products: any) => NewQuoteProduct(products));
    this.priceEscalations = (data.priceEscalations ?? []).map((priceEscalations: any) =>
      NewQuotePriceEscalation(priceEscalations),
    );
    this.user = NewUser(data.user);
    this.integrationSummary =
      // eslint-disable-next-line no-nested-ternary
      (data.integration ?? data.integrationSummary ?? null) === null
        ? null
        : NewIntegrationSummary(data.integration ?? data.integrationSummary);
    this.externalIDs = (data.externalIDs ?? []).map((externalIDs: any) =>
      NewQuoteExternalID(externalIDs),
    );
    this.exportAttempts = (data.exportAttempts ?? []).map((exportAttempts: any) =>
      NewQuoteExportAttempt(exportAttempts),
    );
    this.exportBlockers = (data.exportBlockers ?? []).map((exportBlockers: any) =>
      NewQuoteExportBlocker(exportBlockers),
    );
    this.competitor =
      // eslint-disable-next-line no-nested-ternary
      (data.competitor ?? null) === null ? null : NewQuoteCompetitor(data.competitor);
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.creationDate = DateHelpers.ParseServerNullableDate(data.creationDate);
    this.revisionDate = DateHelpers.ParseServerNullableDate(data.revisionDate);
    this.firstSentAt = DateHelpers.ParseServerNullableDate(data.firstSentAt);
    this.lastSentAt = DateHelpers.ParseServerNullableDate(data.lastSentAt);
    this.revenue = NewCurrency(data.revenue);
    this.estimatedVolume = data.estimatedVolume === undefined ? 0 : data.estimatedVolume;
    this.readyMixQuantity = data.readyMixQuantity === undefined ? '0' : data.readyMixQuantity;
    this.readyMixQuantityUnit =
      data.readyMixQuantityUnit === undefined ? Enums.Unit.CuYd : data.readyMixQuantityUnit;
    this.aggregateQuantity = data.aggregateQuantity === undefined ? '0' : data.aggregateQuantity;
    this.aggregateQuantityUnit =
      data.aggregateQuantityUnit === undefined ? Enums.Unit.Tn : data.aggregateQuantityUnit;
    this.asphaltQuantity = data.asphaltQuantity === undefined ? '0' : data.asphaltQuantity;
    this.asphaltQuantityUnit =
      data.asphaltQuantityUnit === undefined ? Enums.Unit.Tn : data.asphaltQuantityUnit;
    this.lastStatusChangeAt = DateHelpers.ParseServerNullableDate(data.lastStatusChangeAt);
    this.lastStatusChangeBy =
      // eslint-disable-next-line no-nested-ternary
      (data.lastStatusChangeBy ?? null) === null ? null : NewUser(data.lastStatusChangeBy);
    this.revisionOrigin =
      // eslint-disable-next-line no-nested-ternary
      (data.revisionOrigin ?? null) === null ? null : NewQuoteRevisionOrigin(data.revisionOrigin);
  }

  static zero(): Quote {
    const zeroValues: ExcludeMethodsDeep<Quote> = {
      id: NIL_UUID,
      quoteNumber: '',
      purchaseOrderNumber: null,
      projectJobNumber: null,
      name: null,
      revisionNumber: 0,
      version: null,
      status: QuoteStatus.zero(),
      externalID: null,
      restrictOrdersToProjectProducts: false,
      restrictOrdersToProjectMixProducts: false,
      terms: null,
      notes: null,
      expirationDate: null,
      project: Project.zero(),
      plants: [],
      company: null,
      contacts: [],
      quoteConfig: null,
      products: [],
      priceEscalations: [],
      user: User.zero(),
      integrationSummary: null,
      externalIDs: [],
      exportAttempts: [],
      exportBlockers: [],
      competitor: null,
      divisionId: null,
      creationDate: null,
      revisionDate: null,
      firstSentAt: null,
      lastSentAt: null,
      revenue: Currency.zero(),
      estimatedVolume: 0,
      readyMixQuantity: '0',
      readyMixQuantityUnit: Enums.Unit.CuYd,
      aggregateQuantity: '0',
      aggregateQuantityUnit: Enums.Unit.Tn,
      asphaltQuantity: '0',
      asphaltQuantityUnit: Enums.Unit.Tn,
      lastStatusChangeAt: null,
      lastStatusChangeBy: null,
      revisionOrigin: null,
    };
    return new Quote(zeroValues);
  }

  toJSON(): { [key: string]: any } {
    return {
      id: this.id,
      quoteNumber: this.quoteNumber,
      purchaseOrderNumber: this.purchaseOrderNumber,
      projectJobNumber: this.projectJobNumber,
      name: this.name,
      revisionNumber: this.revisionNumber,
      version: this.version,
      status: this.status,
      externalID: this.externalID,
      restrictOrdersToProjectProducts: this.restrictOrdersToProjectProducts,
      restrictOrdersToProjectMixProducts: this.restrictOrdersToProjectMixProducts,
      terms: this.terms,
      notes: this.notes,
      expirationDate: this.expirationDate,
      project: this.project,
      plants: this.plants,
      company: this.company,
      contacts: this.contacts,
      quoteConfig: this.quoteConfig,
      products: this.products,
      priceEscalations: this.priceEscalations,
      user: this.user,
      integration: this.integrationSummary,
      externalIDs: this.externalIDs,
      exportAttempts: this.exportAttempts,
      exportBlockers: this.exportBlockers,
      competitor: this.competitor,
      divisionId: this.divisionId,
      creationDate: this.creationDate,
      revisionDate: this.revisionDate,
      firstSentAt: this.firstSentAt,
      lastSentAt: this.lastSentAt,
      revenue: this.revenue,
      estimatedVolume: this.estimatedVolume,
      readyMixQuantity: this.readyMixQuantity,
      readyMixQuantityUnit: this.readyMixQuantityUnit,
      aggregateQuantity: this.aggregateQuantity,
      aggregateQuantityUnit: this.aggregateQuantityUnit,
      asphaltQuantity: this.asphaltQuantity,
      asphaltQuantityUnit: this.asphaltQuantityUnit,
      lastStatusChangeAt: this.lastStatusChangeAt,
      lastStatusChangeBy: this.lastStatusChangeBy,
      revisionOrigin: this.revisionOrigin,
    };
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  expirationDateDisplay(locale: string): string | null {
    if (this.expirationDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.expirationDate, { locale });
  }

  creationDateDisplay(locale: string): string | null {
    if (this.creationDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.creationDate, { locale });
  }

  revisionDateDisplay(locale: string): string | null {
    if (this.revisionDate === null) {
      return null;
    }
    return DateHelpers.FormatDisplayDateTime(this.revisionDate, { locale });
  }

  filterProducts(kind: Enums.QuoteProductKind): QuoteProduct[] {
    return this.products.filter((qp) => qp.kind === kind);
  }

  /**
   * This will return the quote name as a `.pdf` appended string.
   * If the quote name is "empty", then it will return the project name as a `.pdf` appended string.
   * If the project name is "empty", it will return a default value of `quote.pdf`.
   */
  fileName(): string {
    if (this.name !== undefined && this.name !== null && this.name !== '') {
      return `${this.name}.pdf`;
    }
    if (this.project.name !== undefined && this.project.name !== null && this.project.name !== '') {
      return `${this.project.name}.pdf`;
    }
    return 'quote.pdf';
  }

  /**
   * hasUOMIssues will return true if a single product in this quote has an
   * incompatible mix batch unit.
   */
  hasUOMIssues(): boolean {
    return this.products.some((qp) => qp.product.incompatibleMixQuantityUnits);
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type QuoteWireFormat = {
  id: string;
  quoteNumber: string;
  purchaseOrderNumber: string | null;
  projectJobNumber: string | null;
  name: string | null;
  revisionNumber: number;
  version: number | null;
  status: QuoteStatusWireFormat;
  externalID: string | null;
  restrictOrdersToProjectProducts: boolean;
  restrictOrdersToProjectMixProducts: boolean;
  terms: string | null;
  notes: string | null;
  expirationDate: string | null;
  project: ProjectWireFormat;
  plants: QuotePlantWireFormat[];
  company: CompanyWireFormat | null;
  contacts: ContactWireFormat[];
  quoteConfig: QuoteConfigWireFormat | null;
  products: QuoteProductWireFormat[];
  priceEscalations: QuotePriceEscalationWireFormat[];
  user: UserWireFormat;
  integrationSummary: IntegrationSummaryWireFormat | null;
  externalIDs: QuoteExternalIDWireFormat[];
  exportAttempts: QuoteExportAttemptWireFormat[];
  exportBlockers: QuoteExportBlockerWireFormat[];
  competitor: QuoteCompetitorWireFormat | null;
  divisionId: string | null;
  creationDate: string | null;
  revisionDate: string | null;
  firstSentAt: string | null;
  lastSentAt: string | null;
  revenue: CurrencyWireFormat;
  estimatedVolume: number;
  readyMixQuantity: string;
  readyMixQuantityUnit: string;
  aggregateQuantity: string;
  aggregateQuantityUnit: string;
  asphaltQuantity: string;
  asphaltQuantityUnit: string;
  lastStatusChangeAt: string | null;
  lastStatusChangeBy: UserWireFormat | null;
  revisionOrigin: QuoteRevisionOriginWireFormat | null;
};

type NewQuoteProps = PartialDeep<Quote, { recurseIntoArrays: true }>;
export const NewQuote: (props: NewQuoteProps) => Quote = (props) => new Quote(props);

type NewQuoteFromWireFormatProps = PartialDeep<QuoteWireFormat, { recurseIntoArrays: true }>;
export const NewQuoteFromWireFormat: (props: NewQuoteFromWireFormatProps) => Quote = (props) =>
  new Quote(props);
